import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { useUser } from './UserContext';

export interface Scholarship {
  id: string;
  name: string;
  description: string;
  deadline: string;
  amount: string;
  schoolId: string;
  schoolName: string;
  [key: string]: any;
}

interface ScholarshipsContextType {
  scholarships: Scholarship[];
  loading: boolean;
  error: string | null;
  refreshScholarships: () => void;
}

const ScholarshipsContext = createContext<ScholarshipsContextType | undefined>(undefined);

export const useScholarships = () => {
  const context = useContext(ScholarshipsContext);
  if (context === undefined) {
    throw new Error('useScholarships must be used within a ScholarshipsProvider');
  }
  return context;
};

interface ScholarshipsProviderProps {
  children: ReactNode;
}

export const ScholarshipsProvider: React.FC<ScholarshipsProviderProps> = ({ children }) => {
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const fetchScholarships = async () => {
    // Don't fetch if user is not authenticated
    if (!user) {
      setLoading(false);
      setScholarships([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const db = getDatabase();
      const scholarshipsRef = ref(db, 'scholarship');
      const snapshot = await get(scholarshipsRef);
      if (snapshot.exists()) {
        const scholarshipsData = snapshot.val();
        const scholarshipsArray: Scholarship[] = [];
        for (const schoolId in scholarshipsData) {
          const schoolScholarships = scholarshipsData[schoolId];
          for (const scholarshipId in schoolScholarships) {
            const s = schoolScholarships[scholarshipId];
            scholarshipsArray.push({
              id: scholarshipId,
              name: s['ScholarshipName'] || '',
              description: s['ScholarshipDescription'] || '',
              deadline: s['ScholarshipDueDate'] || '',
              amount: s['ScholarshipValue'] || '',
              schoolId,
              schoolName: s['ScholarshipSchoolName'] || '',
              ...s
            });
          }
        }
        setScholarships(scholarshipsArray);
      } else {
        setScholarships([]);
      }
    } catch (err) {
      console.error('Error fetching scholarships:', err);
      setError('Failed to fetch scholarships data');
    } finally {
      setLoading(false);
    }
  };

  const refreshScholarships = () => {
    fetchScholarships();
  };

  useEffect(() => {
    fetchScholarships();
  }, [user]);

  const value: ScholarshipsContextType = {
    scholarships,
    loading,
    error,
    refreshScholarships,
  };

  return (
    <ScholarshipsContext.Provider value={value}>
      {children}
    </ScholarshipsContext.Provider>
  );
}; 